import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Settings, Users, Bell, FileText, Shield, 
  Workflow, CheckSquare, AlertTriangle, Plus,
  Edit, Trash2, Eye, Save, X, ChevronDown,
  ChevronRight, UserCheck, Mail, Lock, Unlock, Download
} from 'lucide-react';

const SettingsTab = () => {
  const [activeSection, setActiveSection] = useState('workflows');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const sections = [
    { id: 'workflows', label: 'Workflows', icon: Workflow, description: 'Define approval workflows and thresholds' },
    { id: 'approvals', label: 'Approval Matrix', icon: UserCheck, description: 'Configure approval hierarchies' },
    { id: 'templates', label: 'Templates', icon: FileText, description: 'Custom templates and forms' },
    { id: 'notifications', label: 'Notifications', icon: Bell, description: 'Email and system notifications' },
    { id: 'access', label: 'Access Control', icon: Shield, description: 'Role-based permissions' }
  ];

  // Mock data for workflows
  const workflows = [
    {
      id: 'purchase-request',
      name: 'Purchase Request Workflow',
      type: 'Purchase Request',
      steps: [
        { role: 'Department Head', action: 'Review & Approve', required: true },
        { role: 'Finance Manager', action: 'Budget Check', required: true },
        { role: 'Procurement Manager', action: 'Final Approval', required: true }
      ],
      threshold: 50000,
      status: 'active'
    },
    {
      id: 'vendor-approval',
      name: 'Vendor Approval Workflow',
      type: 'Vendor Registration',
      steps: [
        { role: 'Procurement Manager', action: 'Initial Review', required: true },
        { role: 'Legal Team', action: 'Contract Review', required: true },
        { role: 'Finance Director', action: 'Final Approval', required: true }
      ],
      threshold: 0,
      status: 'active'
    }
  ];

  // Mock data for approval matrix
  const approvalMatrix = [
    {
      role: 'Department Head',
      permissions: ['Purchase Requests', 'Budget Approval', 'Vendor Review'],
      maxAmount: 25000,
      canDelegate: true
    },
    {
      role: 'Finance Manager',
      permissions: ['Budget Approval', 'Payment Authorization', 'Cost Analysis'],
      maxAmount: 100000,
      canDelegate: false
    },
    {
      role: 'Procurement Manager',
      permissions: ['Vendor Management', 'Contract Approval', 'Supplier Evaluation'],
      maxAmount: 500000,
      canDelegate: true
    }
  ];

  // Mock data for templates
  const templates = [
    {
      id: 'po-template',
      name: 'Purchase Order Template',
      type: 'Purchase Order',
      description: 'Standard purchase order template with company branding',
      lastModified: '2024-03-15',
      status: 'active'
    },
    {
      id: 'rfq-template',
      name: 'Request for Quotation Template',
      type: 'RFQ',
      description: 'Template for requesting vendor quotations',
      lastModified: '2024-03-10',
      status: 'active'
    }
  ];

  // Mock data for notifications
  const notifications = [
    {
      id: 'low-stock',
      name: 'Low Stock Alert',
      type: 'Email',
      recipients: ['procurement@company.com', 'warehouse@company.com'],
      conditions: 'Stock level below minimum threshold',
      status: 'active'
    },
    {
      id: 'po-approval',
      name: 'PO Approval Required',
      type: 'System',
      recipients: ['approvers'],
      conditions: 'Purchase order awaiting approval',
      status: 'active'
    }
  ];

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      draft: 'bg-yellow-100 text-yellow-800'
    };
    return colors[status] || colors.inactive;
  };

  const renderWorkflows = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Workflow Configuration</h3>
          <p className="text-sm text-gray-500 mt-1">Define approval workflows and thresholds</p>
        </div>
        <button 
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Workflow
        </button>
      </div>

      <div className="grid gap-6">
        {workflows.map((workflow) => (
          <motion.div
            key={workflow.id}
            whileHover={{ scale: 1.01 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h4 className="text-lg font-semibold text-gray-900">{workflow.name}</h4>
                <p className="text-sm text-gray-500">{workflow.type}</p>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(workflow.status)}`}>
                  {workflow.status}
                </span>
                <button className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors">
                  <Edit className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <h5 className="text-sm font-medium text-gray-900 mb-2">Approval Steps</h5>
                <div className="space-y-2">
                  {workflow.steps.map((step, index) => (
                    <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center">
                        <span className="text-xs font-medium text-emerald-600">{index + 1}</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{step.role}</p>
                        <p className="text-xs text-gray-500">{step.action}</p>
                      </div>
                      {step.required && (
                        <CheckSquare className="w-4 h-4 text-emerald-600" />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <span className="text-sm text-gray-500">
                  Threshold: SAR {workflow.threshold.toLocaleString()}
                </span>
                <div className="flex items-center space-x-2">
                  <button className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                    <Eye className="w-4 h-4" />
                  </button>
                  <button className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );

  const renderApprovals = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Approval Matrix</h3>
          <p className="text-sm text-gray-500 mt-1">Configure approval hierarchies and permissions</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
          <Plus className="w-4 h-4" />
          Add Role
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Permissions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Max Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Can Delegate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {approvalMatrix.map((role) => (
                <tr key={role.role} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <UserCheck className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-sm font-medium text-gray-900">{role.role}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {role.permissions.map((permission) => (
                        <span key={permission} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                          {permission}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900">
                      SAR {role.maxAmount.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {role.canDelegate ? (
                      <CheckSquare className="w-4 h-4 text-green-600" />
                    ) : (
                      <X className="w-4 h-4 text-gray-400" />
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderTemplates = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Custom Templates</h3>
          <p className="text-sm text-gray-500 mt-1">Manage document templates and forms</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
          <Plus className="w-4 h-4" />
          New Template
        </button>
      </div>

      <div className="grid gap-6">
        {templates.map((template) => (
          <motion.div
            key={template.id}
            whileHover={{ scale: 1.01 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h4 className="text-lg font-semibold text-gray-900">{template.name}</h4>
                <p className="text-sm text-gray-500">{template.type}</p>
                <p className="text-sm text-gray-600 mt-1">{template.description}</p>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(template.status)}`}>
                  {template.status}
                </span>
                <button className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors">
                  <Edit className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <span className="text-sm text-gray-500">
                Last modified: {new Date(template.lastModified).toLocaleDateString()}
              </span>
              <div className="flex items-center space-x-2">
                <button className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                  <Eye className="w-4 h-4" />
                </button>
                <button className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors">
                  <Download className="w-4 h-4" />
                </button>
                <button className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );

  const renderNotifications = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Notification Settings</h3>
          <p className="text-sm text-gray-500 mt-1">Configure email and system notifications</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
          <Plus className="w-4 h-4" />
          New Notification
        </button>
      </div>

      <div className="grid gap-6">
        {notifications.map((notification) => (
          <motion.div
            key={notification.id}
            whileHover={{ scale: 1.01 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h4 className="text-lg font-semibold text-gray-900">{notification.name}</h4>
                <div className="flex items-center space-x-2 mt-1">
                  {notification.type === 'Email' ? (
                    <Mail className="w-4 h-4 text-blue-500" />
                  ) : (
                    <Bell className="w-4 h-4 text-purple-500" />
                  )}
                  <span className="text-sm text-gray-500">{notification.type}</span>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(notification.status)}`}>
                  {notification.status}
                </span>
                <button className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors">
                  <Edit className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <h5 className="text-sm font-medium text-gray-900 mb-1">Recipients</h5>
                <div className="flex flex-wrap gap-1">
                  {notification.recipients.map((recipient) => (
                    <span key={recipient} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700">
                      {recipient}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <h5 className="text-sm font-medium text-gray-900 mb-1">Conditions</h5>
                <p className="text-sm text-gray-600">{notification.conditions}</p>
              </div>
            </div>

            <div className="flex items-center justify-end pt-4 border-t border-gray-200">
              <div className="flex items-center space-x-2">
                <button className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                  <Eye className="w-4 h-4" />
                </button>
                <button className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );

  const renderAccessControl = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Role-Based Access Control</h3>
          <p className="text-sm text-gray-500 mt-1">Manage user permissions and access levels</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
          <Plus className="w-4 h-4" />
          Add Role
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { role: 'Admin', permissions: 'Full access to all modules', users: 3, color: 'red' },
            { role: 'Manager', permissions: 'Department-level access', users: 12, color: 'blue' },
            { role: 'User', permissions: 'Basic user permissions', users: 45, color: 'green' }
          ].map((role) => (
            <div key={role.role} className="p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-lg font-semibold text-gray-900">{role.role}</h4>
                <div className={`w-3 h-3 rounded-full bg-${role.color}-500`} />
              </div>
              <p className="text-sm text-gray-600 mb-3">{role.permissions}</p>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">{role.users} users</span>
                <button className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors">
                  <Edit className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Section Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex space-x-1">
          {sections.map((section) => {
            const Icon = section.icon;
            return (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeSection === section.id
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title={section.description}
              >
                <Icon className="w-4 h-4" />
                {section.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content based on active section */}
      {activeSection === 'workflows' && renderWorkflows()}
      {activeSection === 'approvals' && renderApprovals()}
      {activeSection === 'templates' && renderTemplates()}
      {activeSection === 'notifications' && renderNotifications()}
      {activeSection === 'access' && renderAccessControl()}
    </div>
  );
};

export default SettingsTab; 